/*
The User service deals with the token and any data contained
within.  It can access, set, or delete the token.  All token
reading, decoding, validation, or other access should go though
this service.
*/
import React from "react";
import JWTDecode from "jwt-decode";
import moment from "moment";

import { TOKEN_REFRESH_MUTATION } from "../../gql/mutations";
import { GET_ME } from "../../gql/queries";

export const LOCAL_STORAGE_TOKEN_KEY = "auth_token";

export const UserContext = React.createContext({
  signedIn: false,
  signInUser: () => {},
  logoutUser: () => {}
});

/**
A helper function to get the claims from a token.
  * @param token {string} an authentication token that can optionally be passed in to avoid a call to local storage, will call local storage if token is not passed.
*/
const getClaims = token => {
  if (!token) {
    token = userGetToken();
  }
  let claims = {};
  if (token) {
    try {
      claims = JWTDecode(token);
    } catch {
      localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    }
  }
  return claims;
};

/**
 * A setter function to set the token in local storage.
 * This call currently does not rerender or update the app and this must be done within the logic that calls this token set.
 * @param token {string} Set the new token in local storage.
 */
export const userSetToken = token => {
  localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);
};

/**
 * A getter function to get what the user's display name is from the token's claims in local storage.
 * We prefer their actual display name but if that is not set we return their email
 * @param token {string} Set the new token in local storage.
 */
export const userGetDisplayName = token => {
  const claims = getClaims(token);
  return claims.username || claims.email || "";
};
/**
  A wrapper around getting a token from local storage.
  */

export const userGetToken = () => {
  return localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY) || "";
};

/**
  Check if a token is still valid.
  * @param token {string} An authentication token that can be optionally passed in to avoid another call to local storage, will call local storage if token is not passed.
  */
export const userTokenValidBefore = () => {
  const claims = getClaims();
  if (claims && claims.exp) {
    const isBefore = moment().isBefore(moment.unix(claims.exp));
    if (!isBefore) {
      userDeleteToken();
    }
    return isBefore;
  }
  return false;
};

/**
 * Init apollo cache
 */
export const userSetApolloCache = client => {
  const claims = getClaims();
  if (claims && claims.exp) {
    const { id, fullName, imageUrl, email, username, rating, role } = claims;
    client.writeQuery({
      query: GET_ME,
      data: {
        me: {
          id,
          fullName,
          imageUrl,
          email,
          username,
          rating,
          role,
          __typename: "User"
        }
      }
    });
    return true;
  }
  return false;
};

/**
  A removal function to clean the token from local storage.
  This call currently does not rerender or update the app and this must
  be done within the logic that calls this token set.
  */
export const userDeleteToken = () => {
  localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
};

/**
 * Refresh a currently valid token.  If there is a token in local storage
 * that is currently valid and gets a new token, saves the token to local storage,
 * and then executes any callback that is passed in. This is handy for things like
 * the constructor in App.tsx because it allows it to be fired off while the constructor
 * loads and then a state update can be fired off when this comes back to rerender the
 * application.
 *
 * Also no callback need be passed in for example in the auth link the token is checked for
 * expiration soon and it will asyncronously request a new token so a user can keep their tab
 * open for a very long time and even without refresh we will continue to fetch valid tokens
 * without interrupting the user experience.
 *
 * @param client {ApolloClient<NormalizedCacheObject>} client to use to execute graphql mutations
 * @param callback? {CallbackFunctionType} optional callback to execute when token refresh has completed, may accept an optional success boolean and should return void
 *
 * @returns {boolean} True if token refresh will be attempted (current token is not expired), False if no token refresh will be attempted as token is currently expired.
 */
export const userRefreshToken = (client, callback) => {
  const token = userGetToken();
  if (!userTokenValidBefore(token)) {
    return false;
  }
  client
    .mutate({
      mutation: TOKEN_REFRESH_MUTATION,
      variables: { token }
    })
    .then(({ data, errors }) => {
      if (errors) {
        if (callback) {
          callback(false);
        }
      } else {
        if (data && data.tokenRefresh && data.tokenRefresh.token) {
          userSetToken(data.tokenRefresh.token);
          if (callback) {
            callback(true);
          }
        } else if (callback) {
          callback(false);
        }
      }
    })
    .catch(() => {
      if (callback) {
        callback(false);
      }
    });
  return true;
};
